import { backendClient } from '../lib/api/backendClient';

import {
  createDefaultLibrarySyncSettings,
  type LibrarySyncAutomationStatus,
  type LibrarySyncSettingsEnvelope,
  type LibrarySyncSettingsUpdate,
  type LibrarySyncSettingsValue,
  type LibrarySyncOneOffStatus,
  type SavedAlbumTracksSyncResult,
  type SavedAlbumRecordingsSyncResult,
  type RecordingWorksSyncResult,
  type RecordingsAndWorksSyncJobStatus,
} from '../interfaces/librarySyncSettings';

const coerceBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  if (typeof value === 'number') return value !== 0;
  return fallback;
};

const coerceStringOrNull = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
};

const normalizeAutomation = (
  raw: unknown,
  fallback: LibrarySyncAutomationStatus
): LibrarySyncAutomationStatus => {
  if (!raw || typeof raw !== 'object') return { ...fallback };

  const candidate = raw as Partial<LibrarySyncAutomationStatus>;
  return {
    enabled: coerceBoolean(candidate.enabled, fallback.enabled),
    updatedAt: coerceStringOrNull(candidate.updatedAt) ?? fallback.updatedAt,
  };
};

const normalizeOneOff = (
  raw: unknown,
  fallback: LibrarySyncOneOffStatus
): LibrarySyncOneOffStatus => {
  if (!raw || typeof raw !== 'object') return { ...fallback };

  const candidate = raw as Partial<LibrarySyncOneOffStatus>;
  return {
    lastRunAt:
      candidate.lastRunAt === null ? null : coerceStringOrNull(candidate.lastRunAt) ?? fallback.lastRunAt,
    lastStatus:
      candidate.lastStatus === null ||
      candidate.lastStatus === 'success' ||
      candidate.lastStatus === 'failed'
        ? candidate.lastStatus ?? fallback.lastStatus
        : fallback.lastStatus,
  };
};

const normalizeSettings = (raw: unknown): LibrarySyncSettingsValue => {
  const normalized = createDefaultLibrarySyncSettings();
  if (!raw || typeof raw !== 'object') {
    return normalized;
  }

  const value = raw as Partial<LibrarySyncSettingsValue>;
  if (typeof value.version === 'number' && Number.isFinite(value.version)) {
    normalized.version = value.version;
  }

  if (value.automations && typeof value.automations === 'object') {
    const automations = value.automations as Record<string, unknown>;
    if ('albumSaveCascade' in automations) {
      normalized.automations.albumSaveCascade = normalizeAutomation(
        automations.albumSaveCascade,
        normalized.automations.albumSaveCascade
      );
    }
    if ('albumUnsaveCascade' in automations) {
      normalized.automations.albumUnsaveCascade = normalizeAutomation(
        automations.albumUnsaveCascade,
        normalized.automations.albumUnsaveCascade
      );
    }
    if ('recordingSaveCascade' in automations) {
      normalized.automations.recordingSaveCascade = normalizeAutomation(
        automations.recordingSaveCascade,
        normalized.automations.recordingSaveCascade
      );
    }
    if ('recordingUnsaveTracks' in automations) {
      normalized.automations.recordingUnsaveTracks = normalizeAutomation(
        automations.recordingUnsaveTracks,
        normalized.automations.recordingUnsaveTracks
      );
    }
    if ('recordingSaveTracks' in automations) {
      normalized.automations.recordingSaveTracks = normalizeAutomation(
        automations.recordingSaveTracks,
        normalized.automations.recordingSaveTracks
      );
    }
    if ('albumSaveRecordings' in automations) {
      normalized.automations.albumSaveRecordings = normalizeAutomation(
        automations.albumSaveRecordings,
        normalized.automations.albumSaveRecordings
      );
    }
    if ('albumUnsaveRecordings' in automations) {
      normalized.automations.albumUnsaveRecordings = normalizeAutomation(
        automations.albumUnsaveRecordings,
        normalized.automations.albumUnsaveRecordings
      );
    }
  }

  if (value.oneOffs && typeof value.oneOffs === 'object') {
    const oneOffs = value.oneOffs as Record<string, unknown>;
    if ('syncAlbumTracks' in oneOffs) {
      normalized.oneOffs.syncAlbumTracks = normalizeOneOff(
        oneOffs.syncAlbumTracks,
        normalized.oneOffs.syncAlbumTracks
      );
    }
    if ('syncAlbumRecordings' in oneOffs) {
      normalized.oneOffs.syncAlbumRecordings = normalizeOneOff(
        oneOffs.syncAlbumRecordings,
        normalized.oneOffs.syncAlbumRecordings
      );
    }
    if ('syncRecordingWorks' in oneOffs) {
      normalized.oneOffs.syncRecordingWorks = normalizeOneOff(
        oneOffs.syncRecordingWorks,
        normalized.oneOffs.syncRecordingWorks
      );
    }
    if ('syncTrackRecordings' in oneOffs) {
      normalized.oneOffs.syncTrackRecordings = normalizeOneOff(
        oneOffs.syncTrackRecordings,
        normalized.oneOffs.syncTrackRecordings
      );
    }
  }

  return normalized;
};

const extractEnvelopeValue = (payload: unknown): LibrarySyncSettingsValue => {
  if (payload && typeof payload === 'object') {
    const envelope = payload as Partial<{ setting?: LibrarySyncSettingsEnvelope }>;
    if (envelope.setting && typeof envelope.setting === 'object') {
      return normalizeSettings(envelope.setting.value);
    }
  }
  return createDefaultLibrarySyncSettings();
};

export const librarySyncSettingsService = {
  async fetchSettings(): Promise<LibrarySyncSettingsValue> {
    try {
      const { data } = await backendClient.get<{ setting?: LibrarySyncSettingsEnvelope }>(
        '/favorites/settings/librarySync'
      );
      return extractEnvelopeValue({ setting: data?.setting });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        return createDefaultLibrarySyncSettings();
      }
      throw error;
    }
  },

  async updateSettings(update: LibrarySyncSettingsUpdate): Promise<LibrarySyncSettingsValue> {
    const { data } = await backendClient.put<{ setting?: LibrarySyncSettingsEnvelope }>(
      '/favorites/settings/librarySync',
      { value: update }
    );
    return extractEnvelopeValue({ setting: data?.setting });
  },

  async runSavedAlbumTracksSync(options: {
    spotifyUserId: string;
    accessToken: string;
  }): Promise<SavedAlbumTracksSyncResult> {
    const spotifyUserId = options.spotifyUserId.trim();
    const accessToken = options.accessToken.trim();

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    const { data } = await backendClient.post<SavedAlbumTracksSyncResult>(
      '/library/sync/saved-album-tracks',
      { spotifyUserId },
      { headers }
    );

    return data;
  },

  async runSavedAlbumRecordingsSync(options: {
    spotifyUserId: string;
    accessToken: string;
  }): Promise<SavedAlbumRecordingsSyncResult> {
    const spotifyUserId = options.spotifyUserId.trim();
    const accessToken = options.accessToken.trim();

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    const { data } = await backendClient.post<SavedAlbumRecordingsSyncResult>(
      '/library/sync/saved-album-recordings',
      { spotifyUserId },
      { headers }
    );

    return data;
  },

  async runRecordingWorksSync(options: {
    spotifyUserId: string;
    accessToken: string;
  }): Promise<RecordingWorksSyncResult> {
    const spotifyUserId = options.spotifyUserId.trim();
    const accessToken = options.accessToken.trim();

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    const { data } = await backendClient.post(
      '/library/sync/recording-works',
      { spotifyUserId },
      { headers }
    );

    return data;
  },

  async startRecordingsAndWorksSync(options: {
    spotifyUserId: string;
    accessToken: string;
    runAlbumRecordings?: boolean;
    runTrackRecordings?: boolean;
  }): Promise<RecordingsAndWorksSyncJobStatus> {
    const spotifyUserId = options.spotifyUserId.trim();
    const accessToken = options.accessToken.trim();
    const payload: Record<string, unknown> = { spotifyUserId };
    if (typeof options.runAlbumRecordings === 'boolean') {
      payload.runAlbumRecordings = options.runAlbumRecordings;
    }
    if (typeof options.runTrackRecordings === 'boolean') {
      payload.runTrackRecordings = options.runTrackRecordings;
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    const { data } = await backendClient.post<RecordingsAndWorksSyncJobStatus>(
      '/library/sync/saved-album-recordings-and-works/start',
      payload,
      { headers }
    );

    return data;
  },

  async getRecordingsAndWorksSyncJobStatus(options: {
    jobId: string;
    spotifyUserId: string;
    accessToken: string;
  }): Promise<RecordingsAndWorksSyncJobStatus> {
    const jobId = options.jobId.trim();
    const spotifyUserId = options.spotifyUserId.trim();
    const accessToken = options.accessToken.trim();

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    const { data } = await backendClient.get<RecordingsAndWorksSyncJobStatus>(
      `/library/sync/saved-album-recordings-and-works/jobs/${jobId}`,
      {
        headers,
        params: {
          spotifyUserId,
        },
      }
    );

    return data;
  },
};
